import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BellRing, Footprints, RotateCcw, AlarmClockOff, AlarmClockMinus, MoonStar } from "lucide-react";
import { toast } from "sonner";
import { EASE } from "@/components/Reveal";

const TOTAL = 10;

export const StepSimulator = () => {
  const [phase, setPhase] = useState("idle"); // idle | ringing | snoozed | done
  const [left, setLeft] = useState(TOTAL);
  const snoozeTimer = useRef(null);

  useEffect(() => () => clearTimeout(snoozeTimer.current), []);

  const start = () => {
    clearTimeout(snoozeTimer.current);
    setLeft(TOTAL);
    setPhase("ringing");
  };

  const step = () => {
    if (phase !== "ringing") return;
    const next = left - 1;
    setLeft(next);
    if (next <= 0) {
      setPhase("done");
      toast.success("Alarm silenced. You walked to wake up.");
    }
  };

  const snooze = () => {
    setPhase("snoozed");
    toast("Snoozed for 5 minutes. Moove never forgets.", { icon: <AlarmClockMinus size={16} /> });
    snoozeTimer.current = setTimeout(() => {
      setPhase("ringing");
      toast("Moove is back. Your steps are still waiting.", { icon: <BellRing size={16} /> });
    }, 3500);
  };

  const reset = () => {
    clearTimeout(snoozeTimer.current);
    setPhase("idle");
    setLeft(TOTAL);
  };

  const pct = Math.round(((TOTAL - left) / TOTAL) * 100);

  return (
    <div
      data-testid="step-simulator"
      className="relative overflow-hidden rounded-[2rem] border border-[#3a322c] bg-espresso p-8 text-cream sm:p-12"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-terra/20 blur-3xl" />
      <div className="relative z-10 flex flex-col items-center text-center">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra">
          Try the mission
        </p>

        <div className="mt-8 flex h-20 items-center justify-center">
          <AnimatePresence mode="wait">
            {phase === "idle" && (
              <motion.div
                key="idle"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex flex-col items-center"
              >
                <MoonStar size={40} className="text-cream/70" />
                <p className="mt-3 text-sm text-cream/60">It's 6:15 AM. You're asleep.</p>
              </motion.div>
            )}
            {phase === "ringing" && (
              <motion.div
                key="ringing"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="flex flex-col items-center"
              >
                <span className="animate-pulse-glow flex h-16 w-16 items-center justify-center rounded-full bg-terra/15">
                  <BellRing size={32} className="animate-ring text-terra" />
                </span>
                <p className="mt-3 font-serif text-xl italic text-cream">Riiing. Time to walk.</p>
              </motion.div>
            )}
            {phase === "snoozed" && (
              <motion.div
                key="snoozed"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex flex-col items-center"
              >
                <MoonStar size={40} className="text-cream/70" />
                <p className="mt-3 font-serif text-xl italic text-cream/80">
                  Snoozed. Enjoy it — it won't last.
                </p>
              </motion.div>
            )}
            {phase === "done" && (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex flex-col items-center"
              >
                <AlarmClockOff size={40} className="text-terra" />
                <p className="mt-3 font-serif text-xl italic text-cream">
                  Alarm off. You're officially up.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {phase !== "idle" && (
          <div className="mt-8 w-full max-w-sm">
            <motion.p
              key={left}
              initial={{ scale: 1.15, opacity: 0.6 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: EASE }}
              data-testid="simulator-steps-remaining"
              className="font-serif text-6xl font-light text-cream"
            >
              {left}
            </motion.p>
            <p className="mt-1 text-xs text-cream/60">
              {phase === "done" ? "goal reached" : "steps remaining"}
            </p>
            <div className="mt-4 h-1.5 w-full rounded-full bg-cream/10">
              <motion.div
                className="h-1.5 rounded-full bg-terra"
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.4, ease: EASE }}
              />
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {phase === "idle" && (
            <button
              data-testid="simulator-start-button"
              onClick={start}
              className="rounded-full bg-terra px-7 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:scale-[1.03] hover:bg-terradark"
            >
              Ring the alarm
            </button>
          )}
          {phase === "ringing" && (
            <>
              <button
                data-testid="demo-simulate-step-button"
                onClick={step}
                className="flex items-center gap-2 rounded-full bg-terra px-7 py-3 text-sm font-semibold text-cream transition-transform duration-200 hover:scale-[1.03] active:scale-95"
              >
                <Footprints size={16} /> Take a step
              </button>
              <button
                data-testid="simulator-snooze-button"
                onClick={snooze}
                className="rounded-full border border-cream/25 px-7 py-3 text-sm font-medium text-cream/80 transition-colors duration-300 hover:border-cream/50"
              >
                Snooze 5m
              </button>
            </>
          )}
          {phase === "snoozed" && (
            <span
              data-testid="simulator-snoozed-note"
              className="rounded-full border border-cream/15 px-6 py-3 text-sm text-cream/50"
            >
              Ringing again in a moment…
            </span>
          )}
          {phase === "done" && (
            <button
              data-testid="simulator-reset-button"
              onClick={reset}
              className="flex items-center gap-2 rounded-full border border-cream/25 px-7 py-3 text-sm font-medium text-cream/80 transition-colors duration-300 hover:border-cream/50"
            >
              <RotateCcw size={15} /> Go back to sleep
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
